/**
 * ActionBar - Floating bottom bar for batch operations
 *
 * Appears when items are selected, provides context-aware actions.
 * Works with SelectionManager for unified selection state.
 *
 * Usage:
 *   import { initActionBar } from '../shared/action-bar.js';
 *   import { ideasSelection } from '../core/selection-manager.js';
 *
 *   initActionBar({
 *     selection: ideasSelection,
 *     actions: [
 *       { id: 'delete', label: 'DELETE', icon: 'trash', handler: handleDelete }
 *     ],
 *     getAllIds: () => getVisibleIdeaIds()
 *   });
 */

// SVG Icons for action buttons
const ICONS = {
  trash: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>`,
  download: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`,
  image: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>`,
  save: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>`,
  selectAll: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg>`,
  clear: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`
};

/**
 * @typedef {Object} ActionConfig
 * @property {string} id - Unique action identifier
 * @property {string} label - Button label text
 * @property {string} [icon] - Icon name from ICONS map
 * @property {Function} handler - Async handler: (selectedIds: string[]) => Promise<void>
 */

/**
 * @typedef {Object} ActionBarConfig
 * @property {import('../core/selection-manager.js').SelectionManager} selection - Selection manager instance
 * @property {string} [containerId] - Container element ID (will create if not exists)
 * @property {ActionConfig[]} [actions] - Action button definitions
 * @property {Function} [getAllIds] - Function to get all selectable IDs: () => string[]
 * @property {Function} [onVisibilityChange] - Callback when bar shows/hides: (isVisible: boolean) => void
 */

/**
 * Initialize action bar for a selection manager
 *
 * @param {ActionBarConfig} config - Configuration object
 * @returns {{ container: HTMLElement, destroy: Function }}
 */
export function initActionBar(config) {
  const {
    selection,
    containerId = 'selection-action-bar',
    actions = [],
    getAllIds = () => [],
    onVisibilityChange = null,
    insertBefore = null  // CSS selector - insert action bar before this element
  } = config;

  if (!selection) {
    console.error('[ActionBar] No selection manager provided');
    return { container: null, destroy: () => {} };
  }

  // Create or get container
  let container = document.getElementById(containerId);
  if (!container) {
    container = document.createElement('div');
    container.id = containerId;
    container.className = 'action-bar';

    // Find the best insertion point based on page structure
    // For dashboard: insert inside .window-body before the grid
    // For generator: insert before #harvest-results
    let inserted = false;

    // Priority 1: Custom insertBefore selector
    if (insertBefore) {
      const target = document.querySelector(insertBefore);
      if (target?.parentNode) {
        target.parentNode.insertBefore(container, target);
        inserted = true;
      }
    }

    // Priority 2: Dashboard - insert inside .window-body before the grid
    if (!inserted) {
      const windowBody = document.querySelector('.window-body');
      const grid = windowBody?.querySelector('#ideas-grid, #gallery-grid, .ideas-grid, .gallery-grid');
      if (windowBody && grid) {
        windowBody.insertBefore(container, grid);
        inserted = true;
      }
    }

    // Priority 3: Generator - insert before harvest results in .phase-content
    if (!inserted) {
      const harvestResults = document.querySelector('#harvest-results');
      if (harvestResults?.parentNode) {
        harvestResults.parentNode.insertBefore(container, harvestResults);
        inserted = true;
      }
    }

    // Fallback: append to content or body
    if (!inserted) {
      const content = document.querySelector('.content') || document.querySelector('main') || document.body;
      content.prepend(container);
    }
  }

  // Render bar structure - COUNT on LEFT, ALL BUTTONS on RIGHT
  const actionButtons = actions.map(action => `
    <button class="action-bar-btn action-bar-${action.id}"
            title="${action.label}"
            data-action="${action.id}">
      ${action.icon ? (ICONS[action.icon] || '') : ''}
      <span>${action.label}</span>
    </button>
  `).join('');

  container.innerHTML = `
    <span class="action-bar-count">0 selected</span>
    <div class="action-bar-buttons">
      <button class="action-bar-btn action-bar-select-all" title="Select All">
        ${ICONS.selectAll}
        <span>SELECT ALL</span>
      </button>
      <button class="action-bar-btn action-bar-clear" title="Clear Selection">
        ${ICONS.clear}
        <span>CLEAR</span>
      </button>
      ${actionButtons}
    </div>
  `;

  // Elements
  const countEl = container.querySelector('.action-bar-count');
  const selectAllBtn = container.querySelector('.action-bar-select-all');
  const clearBtn = container.querySelector('.action-bar-clear');

  // Track previous visibility for change detection
  let wasVisible = false;

  /**
   * Update visibility and count based on selection state
   * @param {{ count: number }} data - Selection data
   */
  function updateBar({ count }) {
    const isVisible = count > 0;

    container.classList.toggle('visible', isVisible);
    countEl.textContent = `${count} selected`;

    // Notify visibility change (e.g., for hiding saved-bar in Phase 4)
    if (wasVisible !== isVisible && onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
    wasVisible = isVisible;
  }

  // Subscribe to selection changes
  const unsubscribe = selection.on('change', updateBar);

  // Select All handler
  selectAllBtn.addEventListener('click', () => {
    const allIds = getAllIds();
    if (allIds.length > 0) {
      selection.selectAll(allIds);
      // Sync DOM state
      selection.syncToDOM('.idea-card, .gallery-card');
    }
  });

  // Clear handler
  clearBtn.addEventListener('click', () => {
    selection.clear();
    // Sync DOM state
    selection.syncToDOM('.idea-card, .gallery-card');
  });

  // Action button handlers
  for (const action of actions) {
    const btn = container.querySelector(`[data-action="${action.id}"]`);
    if (btn && action.handler) {
      btn.addEventListener('click', async () => {
        const selectedIds = selection.getSelected();
        if (selectedIds.length === 0) return;

        // Save original text and set loading state
        const textEl = btn.querySelector('span');
        const originalText = textEl?.textContent || action.label;
        const loadingText = action.loadingLabel || `${action.label.replace(/E$/, 'ING')}...`;

        btn.disabled = true;
        btn.classList.add('is-loading');
        if (textEl) textEl.textContent = loadingText;

        try {
          await action.handler(selectedIds);
        } catch (err) {
          console.error(`[ActionBar] Action '${action.id}' failed:`, err);
        } finally {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          if (textEl) textEl.textContent = originalText;
        }
      });
    }
  }

  // Initial state
  updateBar({ count: selection.size });

  console.log(`[ActionBar] Initialized for ${selection.context} with ${actions.length} actions`);

  return {
    container,
    destroy() {
      unsubscribe();
      container.remove();
    }
  };
}

/**
 * Show a confirmation modal for destructive actions
 *
 * @param {Object} options - Modal options
 * @param {string} options.title - Modal title
 * @param {string} options.message - Modal message
 * @param {string} [options.confirmText='Confirm'] - Confirm button text
 * @param {boolean} [options.danger=false] - Use danger styling
 * @returns {Promise<boolean>} - True if confirmed
 */
export function showConfirmModal({ title, message, confirmText = 'Confirm', danger = false }) {
  return new Promise((resolve) => {
    // Try to use existing modal system
    if (window.DashboardCore?.Modal) {
      window.DashboardCore.Modal.confirm({
        title,
        message,
        confirmText,
        confirmClass: danger ? 'btn-danger' : '',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      });
    } else if (window.showModal) {
      // Generator modal fallback
      window.showModal({
        title,
        content: `<p>${message}</p>`,
        confirmText,
        cancelText: 'Cancel',
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false)
      });
    } else {
      // Last resort: native confirm
      resolve(window.confirm(`${title}\n\n${message}`));
    }
  });
}

/**
 * Show a toast notification
 *
 * @param {string} message - Toast message
 * @param {'success'|'error'|'info'|'warning'} [type='info'] - Toast type
 */
export function showToast(message, type = 'info') {
  if (window.DashboardCore?.Toast) {
    const Toast = window.DashboardCore.Toast;
    if (type === 'error') Toast.error(message);
    else if (type === 'warning') Toast.warning(message);
    else if (type === 'success') Toast.success(message);
    else Toast.info(message);
  } else if (window.showToast) {
    window.showToast(message, type);
  } else {
    console.log(`[Toast:${type}] ${message}`);
  }
}
